import type { CSSProperties } from "react";

/** Matches Tailwind `gap-3` on MetricGrid. */
export const METRIC_GRID_GAP_PX = 12;

/** Matches MetricCard Storybook preview width; cards reflow when the row cannot fit more at this size. */
export const METRIC_CARD_MIN_WIDTH_PX = 220;

/**
 * Tall enough for a 3-line supporting label (Path to Membership / PDR) so loading
 * placeholders and short KPI tiles keep the same footprint as taller cards.
 */
export const METRIC_CARD_MIN_HEIGHT_CLASS = "min-h-[11rem]";

export type MetricGridLayout = "compact" | "balanced" | "expanded" | "stacked";

export const METRIC_GRID_LAYOUT_CLASSES: Record<MetricGridLayout, string> = {
  compact: "grid-cols-1 md:grid-cols-3 2xl:grid-cols-5",
  balanced: "grid-cols-1 md:grid-cols-3 2xl:grid-cols-6",
  expanded: "grid-cols-1 md:grid-cols-4 2xl:grid-cols-5",
  stacked: "grid-cols-1 md:grid-cols-3 2xl:grid-cols-4"
};

const FLUID_GRID_FALLBACK_CLASSES =
  "md:[grid-template-columns:repeat(auto-fill,minmax(var(--metric-card-min-width),1fr))]";

type MetricGridColumnConfig = {
  className: string;
  style?: CSSProperties;
};

export function computeMaxMetricGridColumns(containerWidth: number, minCardWidth: number, gapPx = METRIC_GRID_GAP_PX) {
  return Math.max(1, Math.floor((containerWidth + gapPx) / (minCardWidth + gapPx)));
}

/**
 * Picks a column count that keeps rows balanced when cards wrap (e.g. five cards → 3+2, not 4+1).
 * Multi-card rows that fit stretch to fill; a single card uses maxFit columns so it stays near
 * minCardWidth instead of spanning the full container.
 */
export function computeBalancedMetricGridColumns(
  cardCount: number,
  containerWidth: number,
  minCardWidth: number,
  gapPx = METRIC_GRID_GAP_PX
) {
  if (cardCount <= 0) {
    return 1;
  }

  const maxFit = computeMaxMetricGridColumns(containerWidth, minCardWidth, gapPx);

  if (cardCount === 1) {
    return maxFit;
  }

  if (cardCount <= maxFit) {
    return cardCount;
  }

  const rowCount = Math.ceil(cardCount / maxFit);
  return Math.ceil(cardCount / rowCount);
}

export function getMetricGridColumnConfig({
  layout = "balanced",
  minCardWidth,
  balancedColumnCount,
  isMdUp = true
}: {
  layout?: MetricGridLayout;
  minCardWidth?: number;
  balancedColumnCount?: number | null;
  isMdUp?: boolean;
}): MetricGridColumnConfig {
  if (minCardWidth != null) {
    if (!isMdUp) {
      return { className: "grid-cols-1" };
    }

    if (balancedColumnCount != null && balancedColumnCount > 0) {
      return {
        className: "grid-cols-1",
        style: {
          gridTemplateColumns: `repeat(${balancedColumnCount}, minmax(${minCardWidth}px, 1fr))`
        }
      };
    }

    return {
      className: `grid-cols-1 ${FLUID_GRID_FALLBACK_CLASSES}`,
      style: { "--metric-card-min-width": `${minCardWidth}px` } as CSSProperties
    };
  }

  return { className: METRIC_GRID_LAYOUT_CLASSES[layout] };
}
